import * as React from "react";
import { useState, useEffect } from "react";
import { Edit2, Trash2, Check, X, Variable, Replace } from "lucide-react";
import { ProjectVariable } from "../../../../types/navigationTypes";
import { Button } from "@/components/ui/button";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { cn } from "@/lib/utils";

interface VariableItemProps {
  variable: ProjectVariable;
  onEdit: (variable: ProjectVariable, newValue: string) => void;
  onDelete: (variableId: string) => void;
  onReplace?: (variable: ProjectVariable) => void;
  isReplacing?: boolean;
}

const VariableItem: React.FC<VariableItemProps> = ({ 
  variable, 
  onEdit, 
  onDelete,
  onReplace,
  isReplacing = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(variable.value || '');
  const [isHovered, setIsHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sincronizar editValue cuando cambia la variable
  useEffect(() => {
    if (!isEditing) {
      setEditValue(variable.value || '');
    }
  }, [variable.value, isEditing]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSave = () => {
    if (isSaving) return; // Evitar múltiples llamadas
    
    setIsSaving(true);
    if (editValue !== variable.value) {
      onEdit(variable, editValue);
    }
    setIsEditing(false);
    setIsSaving(false);
  };

  const handleCancel = () => {
    setEditValue(variable.value || '');
    setIsEditing(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Solo guardar si el foco no se movió a un botón de acción
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !relatedTarget.closest('button')) {
      handleSave();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      className={cn(
        "grid grid-cols-[200px_1fr_120px_140px] gap-3 py-3 px-4 items-center border-b border-slate-100 transition-colors bg-white",
        isHovered && "bg-slate-50/80"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Name Column */}
      <div className="flex items-center gap-2 min-w-0">
        <Variable 
          size={ICON_SIZES.sm}
          className="text-slate-400 flex-shrink-0"
        />
        <span className="text-sm font-medium text-slate-700 overflow-hidden text-ellipsis whitespace-nowrap font-mono">
          {variable.name}
        </span>
      </div>

      {/* Value Column */}
      <div 
        className="min-w-0"
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (!isEditing) {
            setIsEditing(true);
          }
        }}
      >
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
            className="w-full py-1 px-2 border border-slate-200 rounded text-xs outline-none font-mono bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        ) : (
          <span 
            className={cn(
              "text-xs overflow-hidden text-ellipsis whitespace-nowrap block font-mono cursor-pointer hover:text-slate-900 transition-colors",
              variable.value ? "text-slate-600" : "text-slate-400"
            )}
            title="Double click to edit"
          >
            {variable.value || '(no value)'}
          </span>
        )}
      </div>

      {/* Updated Column */}
      <div className="text-xs text-slate-500">
        {formatDate(variable.updated_at)}
      </div>

      {/* Actions Column */}
      <div className={cn(
        "flex gap-1 justify-end transition-opacity",
        isEditing || isHovered ? "opacity-100" : "opacity-0"
      )}>
        {isEditing ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              title="Save"
              className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
            >
              <Check size={ICON_SIZES.sm} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              title="Cancel"
              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
            >
              <X size={ICON_SIZES.sm} />
            </Button>
          </>
        ) : (
          <>
            {variable.value && variable.value.trim() !== '' && onReplace && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onReplace(variable);
                }}
                disabled={isReplacing}
                title="Replace in document"
                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
              >
                <Replace 
                  size={ICON_SIZES.sm} 
                  className={isReplacing ? "animate-spin" : ""}
                />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              title="Edit"
              className="h-7 w-7 p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <Edit2 size={ICON_SIZES.sm} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`Are you sure you want to delete the variable "${variable.name}"?`)) {
                  onDelete(variable.id);
                }
              }}
              title="Delete"
              className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
            >
              <Trash2 size={ICON_SIZES.sm} />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VariableItem;

